<template>
  <div class="web-store-card">
    <!-- Left: title + stats -->
    <div class="left-panel">
      <div class="card-header">
        <h3>Web Store</h3>
        <router-link to="/web" class="view-link" title="Open web store">
          <font-awesome-icon icon="globe" />
        </router-link>
      </div>

      <div class="stats">
        <div class="stat">
          <span class="stat-value">{{ liveCarts.length }}</span>
          <span class="stat-label">active cart{{ liveCarts.length !== 1 ? "s" : "" }}</span>
        </div>
        <div class="stat">
          <span class="stat-value">
            <CurrencyFormatter :value="totalCartValue" :currency="currency" />
          </span>
          <span class="stat-label">cart value</span>
        </div>
        <div class="stat">
          <span class="stat-value">{{ websites.length }}</span>
          <span class="stat-label">site{{ websites.length !== 1 ? "s" : "" }}</span>
        </div>
      </div>
    </div>

    <!-- Right: live cart list -->
    <div class="right-panel">
      <p class="panel-label">Live Carts</p>
      <div class="carts-list" v-if="liveCarts.length">
        <div v-for="cart in liveCarts" :key="cart.cart_id" class="cart-row" @click="$router.push('/web')">
          <span class="cart-site-badge" v-if="cart.site">{{ cart.site }}</span>
          <!-- <span class="cart-id">{{ cart.cart_id.slice(0, 10) }}&hellip;</span> -->
          <span class="cart-items-count"> {{ cart.items.length }} item{{ cart.items.length !== 1 ? "s" : "" }} </span>
          <span class="cart-value">
            <CurrencyFormatter :value="cartTotal(cart.items)" :currency="currency" />
          </span>
        </div>
      </div>
      <p class="empty" v-else>No active shoppers right now</p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { CurrencyFormatter } from "@monolith/components";

export default {
  name: "WebStoreCard",
  components: { CurrencyFormatter },
  data() {
    return {
      liveCarts: [],
      cartUpdateRef: null,
    };
  },
  computed: {
    ...mapGetters({
      websites: "websites",
      companySettings: "companySettings",
      socketConnected: "socketConnected",
    }),
    currency() {
      return this.companySettings.currency || "NZD";
    },
    totalCartValue() {
      return this.liveCarts.reduce((sum, cart) => sum + this.cartTotal(cart.items), 0);
    },
  },
  // mounted() {
  //   if (this.socketConnected) {
  //     this.fetchCarts();
  //     this.cartUpdateRef = this.channel.on("cart:updated", this.handleCartUpdate);
  //   }
  // },
  beforeUnmount() {
    this.channel.off("cart:updated", this.cartUpdateRef);
  },
  methods: {
    fetchCarts() {
      this.channel.push("cart:list_all", {}).receive("ok", (reply) => {
        this.liveCarts = reply.carts;
      });
    },
    handleCartUpdate({ cart_id, items, site }) {
      const idx = this.liveCarts.findIndex((c) => c.cart_id === cart_id);
      if (items.length === 0) {
        if (idx !== -1) this.liveCarts.splice(idx, 1);
      } else if (idx !== -1) {
        this.liveCarts[idx] = { cart_id, items, site };
      } else {
        this.liveCarts.push({ cart_id, items, site });
      }
    },
    cartTotal(items) {
      return items.reduce((sum, i) => sum + i.price * i.quantity, 0);
    },
  },

  watch: {
    socketConnected: {
      immediate: true,
      handler(val) {
        if (val && !this.cartUpdateRef) {
          this.fetchCarts();
          this.cartUpdateRef = this.channel.on("cart:updated", this.handleCartUpdate);
        }
      },
    },
  },
};
</script>

<style scoped lang="scss">
.web-store-card {
  display: flex;
  gap: 0;
  min-height: 12rem;
}

// ── Left panel ────────────────────────────────────────────────────────────────
.left-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem;
  min-width: 16rem;
  border-right: 1px solid var(--tertiary-colour);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  h3 {
    margin: 0;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--action-colour);
  }

  .view-link {
    color: var(--text-colour);
    opacity: 0.35;
    font-size: 0.95rem;
    transition: opacity 0.15s, color 0.15s;
    text-decoration: none;
    &:hover {
      opacity: 1;
      color: var(--action-colour);
    }
  }
}

.stats {
  display: flex;
  gap: 0.5rem;
  flex: 1;
  align-items: stretch;
}

.stat {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(--tertiary-colour);
  border-radius: 0.6rem;
  padding: 0.75rem 0.25rem;
  gap: 0.2rem;

  .stat-value {
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--action-colour);
    line-height: 1;
  }

  .stat-label {
    font-size: 0.62rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.5;
    text-align: center;
  }
}

// ── Right panel ───────────────────────────────────────────────────────────────
.right-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1.25rem;
  min-width: 0;
  gap: 0.6rem;
}

.panel-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-colour);
  opacity: 0.45;
  margin: 0;
}

.carts-list {
  flex: 1;
  overflow: auto;
  display: grid;
  grid-template-rows: repeat(2, auto);
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-content: start;
  gap: 0.5rem;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.cart-row {
  flex: 0 0 auto;
  width: 10rem;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.4rem;
  padding: 0.7rem 0.8rem;
  border-radius: 0.5rem;
  background-color: var(--tertiary-colour);
  font-size: 0.82rem;
  color: var(--text-colour);
  min-width: 0;
  cursor: pointer;

  .cart-site-badge {
    font-size: 0.62rem;
    background-color: var(--action-colour);
    color: var(--secondary-text-colour);
    border-radius: 999px;
    padding: 0.1rem 0.45rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    flex-shrink: 0;
    font-weight: 600;
  }

  .cart-id {
    opacity: 0.38;
    font-size: 0.72rem;
    font-family: monospace;
    flex-shrink: 0;
  }

  .cart-items-count {
    opacity: 0.6;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 0.8rem;
  }

  .cart-value {
    margin-top: auto;
    font-weight: 700;
    color: var(--action-colour);
    flex-shrink: 0;
    font-size: 1rem;
  }
}

.empty {
  font-size: 0.85rem;
  opacity: 0.4;
  margin: auto 0;
  font-style: italic;
}

@media only screen and (orientation: portrait) {
  .web-store-card {
    flex-direction: column;
    min-height: unset;
  }

  .left-panel {
    min-width: unset;
    border-right: none;
    border-bottom: 1px solid var(--tertiary-colour);
    padding: 1rem;
  }

  .right-panel {
    padding: 1rem;
  }

  .carts-list {
    flex-direction: column;
    flex-wrap: nowrap;
  }
}
</style>
